import React, { useMemo, useState } from 'react';

const formatCurrency = (val: number) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(val);

const DownPaymentCalculator: React.FC = () => {
    const [price, setPrice] = useState<number>(100000);
    const [downPaymentPct, setDownPaymentPct] = useState<number>(10);
    const [rate, setRate] = useState<number>(13);
    const [processingPct, setProcessingPct] = useState<number>(3);
    const [tenure, setTenure] = useState<number>(5);

    const { downPayment, loanAmount, processingFee, cashNeeded, emi } = useMemo(() => {
        const dp = Math.round((downPaymentPct / 100) * price);
        const loan = Math.max(0, price - dp);
        const pf = Math.round((processingPct / 100) * loan);
        const cash = dp + pf;

        const monthlyRate = rate / 12 / 100;
        const n = Math.max(1, tenure) * 12;
        let emiVal = 0;
        if (loan > 0) {
            if (monthlyRate === 0) emiVal = loan / n;
            else {
                const rPow = Math.pow(1 + monthlyRate, n);
                emiVal = (loan * monthlyRate * rPow) / (rPow - 1);
            }
        }

        return {
            downPayment: dp,
            loanAmount: loan,
            processingFee: pf,
            cashNeeded: cash,
            emi: Math.round(emiVal)
        };
    }, [price, downPaymentPct, processingPct, rate, tenure]);

    return (
        <div className="relative w-full">
            {/* Center pill title */}
            <div className="flex justify-center -mt-8 mb-4 pointer-events-none">
                <div className="bg-white rounded-full px-6 py-2 shadow-[0_10px_20px_rgba(0,27,74,0.15)] text-[#001b4a] font-semibold text-base" style={{ boxShadow: '0 10px 30px rgba(0,27,74,0.12)' }}>
                    Down Payment Calculator
                </div>
            </div>

            <div className="bg-transparent">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
                    {/* Left card - inputs */}
                    <div className="lg:col-span-7">
                        <div className="bg-white rounded-2xl border border-slate-200 p-6 md:p-8 purple-card-shadow">
                                <div className="space-y-4">
                                    <div className="flex items-center justify-between">
                                    <label className="text-sm font-semibold text-slate-700">Total cost of asset (₹)*</label>
                                    <input type="number" value={price} onChange={(e) => setPrice(Number(e.target.value))} className="w-28 text-right bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 font-bold text-slate-900 focus:ring-2 focus:ring-[#f1ae24] outline-none" />
                                </div>
                                <div className="flex items-center justify-between">
                                    <label className="text-sm font-semibold text-slate-700">Down payment (in %)*</label>
                                    <input type="number" value={downPaymentPct} onChange={(e) => setDownPaymentPct(Number(e.target.value))} className="w-20 text-right bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 font-bold text-slate-900 focus:ring-2 focus:ring-[#f1ae24] outline-none" />
                                </div>
                                <div className="flex items-center justify-between">
                                    <label className="text-sm font-semibold text-slate-700">Interest rate (in %)*</label>
                                    <input type="number" value={rate} onChange={(e) => setRate(Number(e.target.value))} className="w-20 text-right bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 font-bold text-slate-900 focus:ring-2 focus:ring-[#f1ae24] outline-none" />
                                </div>
                                <div className="flex items-center justify-between">
                                    <label className="text-sm font-semibold text-slate-700">Processing fee (in %)*</label>
                                    <input type="number" value={processingPct} onChange={(e) => setProcessingPct(Number(e.target.value))} className="w-20 text-right bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 font-bold text-slate-900 focus:ring-2 focus:ring-[#f1ae24] outline-none" />
                                </div>
                                <div className="flex items-center justify-between">
                                    <label className="text-sm font-semibold text-slate-700">Loan tenure (in years)*</label>
                                    <input type="number" value={tenure} onChange={(e) => setTenure(Number(e.target.value))} className="w-20 text-right bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 font-bold text-slate-900 focus:ring-2 focus:ring-[#f1ae24] outline-none" />
                                </div>

                                    <div>
                                    <input type="range" min={1} max={30} value={tenure} onChange={(e) => setTenure(Number(e.target.value))} className="w-full h-2 appearance-none rounded-lg accent-[#001b4a]" />
                                    <div className="flex justify-between text-xs text-slate-400 mt-2">
                                        <span>1 Years</span>
                                        <span>30 Years</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Right summary - orange bordered */}
                    <div className="lg:col-span-5">
                        <div className="rounded-2xl p-1" style={{ background: 'linear-gradient(90deg,#001b4a,#0b3b82)' }}>
                            <div className="bg-white rounded-2xl p-6 h-full">
                                <h3 className="text-lg font-bold text-[#001b4a] mb-6 text-center uppercase tracking-wider">Calculation Summary</h3>
                                <div className="space-y-4">
                                    <div className="flex justify-between items-center border-b border-slate-100 pb-3">
                                        <div className="text-sm font-medium text-slate-500">Down payment (DP)</div>
                                        <div className="text-base font-bold text-slate-900">{formatCurrency(downPayment)}</div>
                                    </div>

                                    <div className="flex justify-between items-center border-b border-slate-100 pb-3">
                                        <div className="text-sm font-medium text-slate-500">Loan amount (LA)</div>
                                        <div className="text-base font-bold text-slate-900">{formatCurrency(loanAmount)}</div>
                                    </div>

                                    <div className="flex justify-between items-center border-b border-slate-100 pb-3">
                                        <div className="text-sm font-medium text-slate-500">Processing fee (PF)</div>
                                        <div className="text-base font-bold text-slate-900">{formatCurrency(processingFee)}</div>
                                    </div>

                                    <div className="flex justify-between items-center border-b border-slate-100 pb-3">
                                        <div className="text-sm font-medium text-slate-500">Cash needed (DP + PF)</div>
                                        <div className="text-base font-bold text-slate-900">{formatCurrency(cashNeeded)}</div>
                                    </div>

                                    <div className="flex justify-between items-center pt-2">
                                        <div className="text-base font-bold text-[#001b4a]">Monthly EMI</div>
                                        <div className="text-xl font-bold text-[#f1ae24]">{formatCurrency(emi)}</div>
                                    </div>
                                </div></div>
                            </div>
                        </div>
                    </div>
                </div>

            <style>{`
                input[type='range']::-webkit-slider-runnable-track { height: 6px; border-radius: 999px; background: #eee; }
                input[type='range']::-webkit-slider-thumb { -webkit-appearance: none; height: 18px; width: 18px; border-radius: 50%; background: #001b4a; border: 4px solid #fff; margin-top: -6px; box-shadow: 0 6px 14px rgba(0,27,74,0.18); }
            `}</style>
        </div>
    );
};

export default DownPaymentCalculator;
